import React, { useEffect, useState } from "react";
import { Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Container, MenuItem, CircularProgress, Grid, Box, Autocomplete } from "@mui/material";
import { useNavigate } from "react-router-dom";
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';

import Products from "../../collections/products";
import Inventory from "../../models/inventory";
import { Report } from "../../models/report";

const AddInventoryLevelTransactions = (props) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingYesterday, setLoadingYesterday] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [quantity, setQuantity] = useState('');

  useEffect(() => {
    if (mounted) {
      return;
    }
    setMounted(true);

    props.setPageTitle("Nhập hàng");

    const fetchProducts = async () => {
      const listProducts = new Products();
      await listProducts.fetch();
      setProducts(listProducts.items);
    }
    fetchProducts();
    setLoading(false);
  })

  const handleAddTransaction = () => {
    if (!selectedProduct) {
      toast.error("Vui lòng chọn sản phẩm!", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    const quantityNum = parseFloat(quantity);
    if (!quantity || isNaN(quantityNum) || quantityNum <= 0) {
      toast.error("Vui lòng nhập số lượng lớn hơn 0!", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    setTransactions([...transactions, {
      product: selectedProduct,
      quantity: quantityNum,
    }]);

    // Reset form
    setSelectedProduct(null);
    setQuantity('');
  };

  const handleRemoveTransaction = (index) => {
    setTransactions(transactions.filter((_, i) => i !== index));
  };

  const handleLoadYesterdayInventory = async () => {
    try {
      setLoadingYesterday(true);
      const dateRange = Report.getYesterdayDateRange();
      const inventoryReport = await Report.fetch('inventory', dateRange);

      // Clear existing transactions
      setTransactions([]);

      // Create new transactions from yesterday's inventory
      const newTransactions = inventoryReport.products
        .filter(item => item.total_quantity > 0)  // Only include items with positive quantity
        .map(item => {
          const product = products.find(p => p.id === item.product_id);
          console.log('Matching product for ID', item.product_id, ':', product);
          if (!product) {
            console.warn(`Product not found for ID: ${item.product_id}`);
          }
          return {
            product: product,
            quantity: item.total_quantity
          };
        })
        .filter(transaction => transaction.product); // Ensure product exists

      console.log('Generated transactions:', newTransactions);

      setTransactions(newTransactions);

      toast.success("Đã tải dữ liệu tồn kho ngày hôm qua!", {
        position: "top-center",
        autoClose: 3000,
      });
    } catch (error) {
      toast.error("Không thể tải dữ liệu tồn kho ngày hôm qua!", {
        position: "top-center",
        autoClose: 3000,
      });
      console.error("Error loading yesterday's inventory:", error);
    } finally {
      setLoadingYesterday(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting || transactions.length === 0) {
      toast.error("Vui lòng thêm ít nhất một giao dịch!", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    const invalidTransactions = transactions.filter(t => !t.quantity || t.quantity <= 0);
    if (invalidTransactions.length > 0) {
      toast.error("Có sản phẩm có số lượng không hợp lệ. Vui lòng kiểm tra lại!", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    try {
      setSubmitting(true);
      let hasError = false;

      for (const transaction of transactions) {
        const inventoryLevelTransaction = new Inventory({
          product_id: transaction.product.id,
          quantity: transaction.quantity,
          transaction_type: 'addition',
        });

        const res = await inventoryLevelTransaction.addInventoryLevelTransactions();

        if (!res.success) {
          toast.error(`Lỗi khi thêm ${transaction.product.name}: ${res.messenge}`, {
            position: "top-center",
            autoClose: 3000,
          });
          hasError = true;
          break;
        }
      }

      if (!hasError) {
        toast.success("Tất cả giao dịch đã được lưu thành công!", {
          position: "top-center",
          autoClose: 3000,
        });
        // Reset the form
        setTransactions([]);
        setSelectedProduct(null);
        setQuantity('');
      }
    } catch (error) {
      let errorMessage = "Đã xảy ra lỗi không xác định!";
      if (error.response?.data?.error) {
        if (Array.isArray(error.response.data.error)) {
          errorMessage = error.response.data.error.join(", ");
        } else if (typeof error.response.data.error === "string") {
          errorMessage = error.response.data.error;
        } else if (typeof error.response.data.error === "object") {
          errorMessage = JSON.stringify(error.response.data.error);
        }
      }
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 3000,
      });
    }
  }

  if (loading) {
    return <CircularProgress />;
  }
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 3, boxShadow: 1 }}>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleLoadYesterdayInventory}
          disabled={loadingYesterday}
          sx={{
            mb: 3,
            backgroundColor: 'secondary.main',
            '&:hover': {
              backgroundColor: 'secondary.dark',
            }
          }}
        >
          {
            loadingYesterday ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Tải dữ liệu tồn kho hôm qua"
            )
          }
        </Button >
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={5}>
            <Autocomplete
              value={selectedProduct}
              options={products.filter(product =>
                !transactions.some(transaction => transaction.product.id === product.id)
              )}
              getOptionLabel={(option) => option.name}
              onChange={(event, value) => {
                setSelectedProduct(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Chọn sản phẩm"
                  variant="outlined"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              label="Nhập số lượng"
              variant="outlined"
              fullWidth
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              inputProps={{
                step: "0.01",
                min: "0.01"
              }}
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleAddTransaction}
              sx={{
                height: '56px',
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }}
            >
              Thêm
            </Button>
          </Grid>

          {
            transactions.length > 0 && (
              <Grid item xs={12}>
                <TableContainer sx={{
                  mt: 2,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 1,
                  '& .MuiTableCell-head': {
                    backgroundColor: 'grey.100',
                    fontWeight: 'bold'
                  }
                }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>STT</TableCell>
                        <TableCell>Sản phẩm</TableCell>
                        <TableCell align="right">Số lượng</TableCell>
                        <TableCell align="right">Thao tác</TableCell>
                      </TableRow>
                    </TableHead >
                    <TableBody>
                      {transactions.map((transaction, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{transaction.product.name}</TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              value={transaction.quantity === 0 ? "" : transaction.quantity}
                              onChange={(e) => {
                                const newValue = e.target.value === "" ? "" : parseFloat(e.target.value);
                                const newTransactions = [...transactions];
                                newTransactions[index] = {
                                  ...transaction,
                                  quantity: newValue
                                };
                                setTransactions(newTransactions);
                              }}
                              size="small"
                              inputProps={{
                                step: "0.01",
                                min: "0.01"
                              }}
                              sx={{ width: '100px' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              color="error"
                              onClick={() => handleRemoveTransaction(index)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'error.light',
                                  color: 'white'
                                }
                              }}
                            >
                              Xóa
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody >
                  </Table >
                </TableContainer >
              </Grid >
            )
          }

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
              disabled={transactions.length === 0 || submitting}
              sx={{
                mt: 2,
                py: 1.5,
                fontSize: '1.1rem',
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '&:disabled': {
                  backgroundColor: 'grey.300',
                }
              }}
            >
              Lưu tất cả giao dịch
            </Button>
          </Grid>
        </Grid >
      </Box >
    </Container >
  );
}
export default AddInventoryLevelTransactions;
